import React from 'react';
import { createPortal } from 'react-dom';
import { MODAL_OVERLAY_INSET } from './modalStyles';

/**
 * Modal component for consistent modal behaviour across the application.
 * Defaults to a centred, inset dialog on all breakpoints so mobile panels
 * never run edge-to-edge. The overlay does not scroll — only the panel body does.
 *
 * @param {'inset'|'sheet'} [layout='inset'] — `sheet` anchors to the bottom
 *   (still inset horizontally); `inset` centres vertically.
 */
const Modal = ({
  isOpen,
  onClose,
  children,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  layout = 'inset',
  className = '',
}) => {
  React.useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isSheet = layout === 'sheet';

  const overlayClasses = [
    'fixed inset-0 z-50 flex justify-center overflow-hidden bg-black/40',
    MODAL_OVERLAY_INSET,
    isSheet ? 'items-end' : 'items-center',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const innerClasses = 'flex w-full max-w-full min-h-0 justify-center';

  const modalContent = (
    <div
      className={overlayClasses}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={innerClasses}>{children}</div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
